import apiClient from "../../services/apiClient";
import { mobileAppUri, dataLoaderUri } from "../API";

// WordDescriptionsAPI.tsx
export interface WordDescriptionExercise {
  sectionId: string;           // PK: which lesson or section
  exerciseId: string;          // SK: unique per-exercise
  wordId: string;              // the vocabulary word being described
  baseLanguage: string;        // e.g. "en" (language for descriptions/hints)
  targetLanguage: string;      // e.g. "es" (language for answer options)
  domain: string;              // e.g.: general, construction, medical
  proficiencyLevel: string;    // A1…C2
  difficulty: string;          // 1=easy,2=medium,3=hard
  options: string[];           // multiple choice options in target language
  correctOption: string;       // correct answer in target language
  hints: Array<{              // hints/descriptions
    baseText: string;          // hint in base language
    targetText: string;        // hint in target language
  }> | string[];              // backward compatibility for old format
  createdAt: string;           // ISO timestamp
}

// Fetch word description exercises for a specific section
export const fetchWordDescriptionExercises = async (sectionName: string): Promise<WordDescriptionExercise[]> => {
  try {
    const data = await apiClient.get(`${mobileAppUri}/api/v1/item-description-exercises`, {
      params: { section: sectionName }
    });
    
    // Check if the response has an 'items' property (backend format)
    if (data && Array.isArray(data.items)) {
      return data.items;
    }
    
    // Fallback: if it's already an array, return it directly
    if (Array.isArray(data)) {
      return data;
    }
    
    // If neither format matches, return empty array
    console.warn('Unexpected response format from word description exercises API:', data);
    return [];
  } catch (error) {
    console.error('Error fetching word description exercises:', error);
    throw error;
  }
};

// Create a new word description exercise
export const createWordDescriptionExercise = async (exercise: Omit<WordDescriptionExercise, 'createdAt'>): Promise<{message: string}> => {
  try {
    const data = await apiClient.post(`${dataLoaderUri}/api/v1/item-description-exercises`, {
      sectionId: exercise.sectionId,
      exerciseId: exercise.exerciseId,
      wordId: exercise.wordId,
      baseLanguage: exercise.baseLanguage,
      targetLanguage: exercise.targetLanguage,
      domain: exercise.domain,
      proficiencyLevel: exercise.proficiencyLevel,
      difficulty: exercise.difficulty,
      options: exercise.options,
      correctOption: exercise.correctOption,
      hints: exercise.hints
    });
    return data;
  } catch (error) {
    console.error('Error creating word description exercise:', error);
    throw error;
  }
};

// Update an existing word description exercise
export const updateWordDescriptionExercise = async (sectionId: string, exerciseId: string, exercise: Partial<WordDescriptionExercise>): Promise<{message: string}> => {
  try {
    const data = await apiClient.put(`${dataLoaderUri}/api/v1/item-description-exercises/${sectionId}/${exerciseId}`, exercise);
    return data;
  } catch (error) {
    console.error('Error updating word description exercise:', error);
    throw error;
  }
};

// Delete a word description exercise
export const deleteWordDescriptionExercise = async (sectionId: string, exerciseId: string): Promise<void> => {
  try {
    await apiClient.delete(`${dataLoaderUri}/api/v1/item-description-exercises/${sectionId}/${exerciseId}`);
  } catch (error) {
    console.error('Error deleting word description exercise:', error);
    throw error;
  }
};